import { z } from 'zod'

import {
  FORCE_CURRENT_USER,
  getEntityRegistry,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import {
  checkRateLimit,
  recordCall,
} from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/write/rate-limiter'
import { clearToolCache } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-cache'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type {
  SessionContext,
  ToolResultContent,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import {
  requireDataClassName,
  resolveDataClass,
} from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'

type RelateEntityArgs = {
  parentDataClass: string
  parentId: string
  relation: string
  targetId: string
}

export const relateEntityTool = defineTool({
  name: 'relate_entity',
  description:
    'Connect two CRM records via a junction relation. For example, connect a company to an activity, or a person to an opportunity. ' +
    'Use the relation name as defined in the registry (e.g. "activities", "persons", "companies"). ' +
    'Shows a confirmation card before writing. ' +
    'Do NOT use this for fields that are direct attributes on the record — use update_entity for those. ' +
    'targetId must come from a COMPLETED create_entity result — never call this in the same turn as the create it depends on, and never pass a placeholder or guessed id. ' +
    'To connect the current user ("relate this to me", "add me", "link me"), pass targetId="@currentUser" — do NOT ask the user for their name and do NOT look up or copy the user id from context. ' +
    'Use the relation that points at people (e.g. "persons") for current-user links.',
  inputSchema: () =>
    z.object({
      parentDataClass: z
        .string()
        .describe('Entity type of the parent record, e.g. Company, Activity'),
      parentId: z.string().describe('The _id of the parent record'),
      relation: z
        .string()
        .describe(
          'The relation name as defined in the registry, e.g. activities, persons, companies',
        ),
      targetId: z
        .string()
        .describe(
          `The _id of the record to connect. Pass the literal "${FORCE_CURRENT_USER}" to connect the acting (current) user — it resolves to their id server-side.`,
        ),
    }),
  concurrencySafe: false,
  cacheable: false,

  async run(
    rawArgs: unknown,
    ctx: SessionContext,
    toolCallId?: string,
  ): Promise<ToolResultContent | { error: string }> {
    // FunctionDefinition<unknown> contravariance — cast args inside run(), per project pattern
    const args = rawArgs as RelateEntityArgs
    const { parentDataClass, parentId, relation, targetId } = args

    // Resolve the "@currentUser" sentinel to the acting user's id so
    // "relate this to me" works without the model copying a 64-char id or
    // asking the user their name. Mirrors forceAtCreate's FORCE_CURRENT_USER.
    let resolvedTargetId = targetId
    if (targetId === FORCE_CURRENT_USER) {
      if (!ctx.currentUserId) {
        return { error: 'Could not determine the current user to connect.' }
      }
      resolvedTargetId = ctx.currentUserId
    }

    const validParentClass = requireDataClassName(parentDataClass)
    const meta = validParentClass
      ? getEntityRegistry()[validParentClass]
      : undefined
    if (!meta) {
      return { error: `Unknown entity type: ${parentDataClass}` }
    }

    const relationSpec = meta.relations[relation]
    if (!relationSpec) {
      return {
        error: `Unknown relation "${relation}" for ${parentDataClass}. Available relations: ${Object.keys(meta.relations).join(', ')}.`,
      }
    }

    const { viaJunction } = relationSpec
    if (!viaJunction) {
      return {
        error: `Relation "${relation}" on ${parentDataClass} is a direct filter relation, not a junction — use update_entity to set the field directly.`,
      }
    }

    const rateCheck = checkRateLimit('creates')
    if (!rateCheck.allowed) {
      return { kind: 'text', content: rateCheck.message! }
    }

    // Resolve display names for the confirmation card
    const parentDc = validParentClass
      ? resolveDataClass(validParentClass)
      : null
    const targetDc = resolveDataClass(relationSpec.dataClass)
    const { data } = getHostRuntime()
    const [parentItem, targetItem] = await Promise.all([
      parentDc ? data.getResolved(parentDc, parentId).catch(() => null) : null,
      targetDc
        ? data.getResolved(targetDc, resolvedTargetId).catch(() => null)
        : null,
    ])
    const parentName =
      (parentItem as { get: (f: string) => unknown } | null)?.get(
        'displayTitle',
      ) ?? parentId
    const targetName =
      (targetItem as { get: (f: string) => unknown } | null)?.get(
        'displayTitle',
      ) ?? resolvedTargetId

    // Existence check — don't create a duplicate connection (and don't prompt to
    // confirm one that already exists). Query the junction for a row linking this
    // exact pair; also check the mirror (father/contact swapped) so symmetric
    // contact-contact relations already stored from the other side are caught. The
    // mirror query matches nothing for one-directional junctions (e.g. documents),
    // so running it generically is safe.
    const jc = viaJunction.junctionClass
    const exact = await data.query(jc, {
      filters: {
        [viaJunction.parentFilterKey]: parentId,
        [viaJunction.idField]: resolvedTargetId,
      },
      limit: 1,
    })
    const mirror =
      exact.length > 0
        ? []
        : await data.query(jc, {
            filters: {
              [viaJunction.parentFilterKey]: resolvedTargetId,
              [viaJunction.idField]: parentId,
            },
            limit: 1,
          })
    if (exact.length > 0 || mirror.length > 0) {
      return {
        kind: 'text',
        content: `${String(parentName)} (${parentDataClass}) and ${String(targetName)} (${relationSpec.dataClass}) are already connected.`,
      }
    }

    const toolId = toolCallId ?? '_unknown'
    const confirmed = await new Promise<boolean>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        toolId,
        {
          type: 'confirm',
          toolCallId: toolId,
          spec: {
            title: `Connect ${parentDataClass}`,
            rows: [
              {
                label: parentDataClass,
                value: String(parentName),
                source: 'resolved' as const,
              },
              {
                label: relationSpec.dataClass,
                value: String(targetName),
                source: 'resolved' as const,
              },
            ],
          },
        },
        (result: unknown) => resolve(result === true),
        reject,
      )
    })

    if (!confirmed) {
      return {
        kind: 'text',
        content: `Cancelled connecting ${parentDataClass} to ${relationSpec.dataClass}.`,
      }
    }

    const jdc = resolveDataClass(viaJunction.junctionClass)
    if (!jdc) {
      return {
        error: `No DataClass found for junction ${viaJunction.junctionClass}`,
      }
    }

    try {
      await data.create(viaJunction.junctionClass, {
        [viaJunction.parentFilterKey]: parentId,
        [viaJunction.idField]: resolvedTargetId,
        ...(viaJunction.parentFilterExtra ?? {}),
      })
      recordCall('creates')
      clearToolCache()
      // Refresh the parent's Scrivito query caches — its chain includes the
      // relation classes that back the detail-page tabs, so the new connection
      // shows without a manual reload.
      if (validParentClass) {
        data.invalidate(validParentClass)
      }
      return {
        kind: 'text',
        content: `Successfully connected ${String(parentName)} (${parentDataClass}) to ${String(targetName)} (${relationSpec.dataClass}).`,
      }
    } catch {
      return {
        kind: 'text',
        content: `Failed to connect ${parentDataClass} to ${relationSpec.dataClass} — please try again.`,
      }
    }
  },
})
